import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, CardActions, CardContent, TextField, Typography
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { trTR } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

import useApiService from '../../component/hooks/useApiService';
import { useAppSnackbar } from '../../component/providers/SnackbarProvider';
import Select from '../../component/ui/select/select';

import { apiUrls } from '../../constans/apiUrls';
import { LIMIT_TYPES } from '../../constans/parameter';

// ---------------- helpers ----------------
const asDayjsOrNull = (v) => (v ? dayjs(v) : null);
const toIso = (d) => (d ? dayjs(d).toISOString() : null);

const blockInvalidNumberKeys = (e) => {
  if (['e', 'E', '-', '+'].includes(e.key)) e.preventDefault();
};

const sanitizePositiveInt = (v) => String(v ?? '').replace(/[^\d]/g, '');

const sanitizePositiveDecimal = (v) => {
  let s = String(v ?? '').replace(',', '.').replace(/[^0-9.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return s;
};

// ---- Defaults
const DEFAULT_TIERS = {
  basic: { quantity: '', value: '' },
  standard: { quantity: '', value: '' },
  advanced: { quantity: '', value: '' },
  elite: { quantity: '', value: '' },
  special: { quantity: '', value: '' },
};

const parseIncomingValue = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return { num: '', unit: null };
  const m = s.match(/^[-+]?\d+(?:[.,]\d+)?/);
  const num = m ? m[0].replace(',', '.') : '';
  const unit = s.includes('%') ? 'PERCENT' : (s.includes('$') ? 'AMOUNT' : null);
  return { num, unit };
};

const normalizeTierIn = (key, incoming) => {
  if (!incoming || typeof incoming !== 'object') return { ...DEFAULT_TIERS[key] };
  const { num, unit } = parseIncomingValue(incoming.value);
  return {
    quantity: incoming.quantity ?? '',
    value: num,
    rule_id: incoming.rule_id,
    __unit: unit,
  };
};

const normalizeIncomingTiers = (incomingTiers) => {
  const keys = ['basic', 'standard', 'advanced', 'elite', 'special'];
  const merged = {};
  for (const k of keys) {
    if (!incomingTiers?.[k]) continue;
    merged[k] = normalizeTierIn(k, incomingTiers[k]);
  }
  return merged;
};

const withSymbol = (rawValue, limitType) => {
  const valStr = String(rawValue ?? '').trim();
  if (!valStr) return '';
  return limitType === 'AMOUNT' ? `${valStr}$` : `${valStr}%`;
};

const sanitizeTierOut = (tier, limitType, includeQuantity = true) => {
  const out = {};
  const qStr = String(tier?.quantity ?? '').trim();
  const vStr = String(tier?.value ?? '').trim();

  if (includeQuantity && qStr !== '') {
    out.quantity = Number(qStr);
  }
  if (vStr !== '') {
    out.value = withSymbol(vStr, limitType);
  }
  if (tier?.rule_id != null) out.rule_id = tier.rule_id;
  return out;
};

const stripSpecialForBucket = (bucket, tiersObj) => {
  if (bucket !== 'special_offer') {
    const { special, ...rest } = tiersObj || {};
    return rest;
  }
  return tiersObj;
};

const buildNonSpecialTiers = (tiers, limitType) =>
  Object.fromEntries(
    Object.entries(tiers || {})
      .filter(([key]) => key !== 'special')
      .filter(([, t]) => {
        const hasQty = String(t?.quantity ?? '').trim() !== '';
        const hasVal = String(t?.value ?? '').trim() !== '';
        return hasQty || hasVal;
      })
      .map(([key, t]) => [key, sanitizeTierOut(t, limitType, true)])
  );

const inferLimitTypeFromTiers = (tiers) => {
  const units = Object.values(tiers || {})
    .map((t) => t?.__unit)
    .filter(Boolean);
  if (!units.length) return null;
  const first = units[0];
  return units.every((u) => u === first) ? first : null;
};

const CreateBoost = ({ data, close, adminPanelAction, openType, bucket }) => {
  const { executeGet, executePost } = useApiService();
  const { showSnackbar } = useAppSnackbar();

  const [boostCategories, setBoostCategories] = useState([]);
  const [viewMode, setViewMode] = useState(false);

  const [selectedLimitType, setSelectedLimitType] =
    useState(LIMIT_TYPES?.[0]?.Code || 'AMOUNT');

  const [dataModel, setDataModel] = useState({
    category_code: '',
    category_name: '',
    variant_id: undefined,
    bucket,
    tiers: { ...DEFAULT_TIERS },
    PeriodDays: '',
    valid_from: null,
    valid_until: null,
    isActive: true,
  });

  const setField = (fields) => setDataModel((s) => ({ ...s, ...fields }));

  const setTierQty = (tierKey, value) => {
    setDataModel((s) => ({
      ...s,
      tiers: {
        ...s.tiers,
        [tierKey]: { ...(s.tiers[tierKey] || {}), quantity: value },
      },
    }));
  };

  const setTierLimit = (tierKey, value) => {
    setDataModel((s) => ({
      ...s,
      tiers: {
        ...s.tiers,
        [tierKey]: { ...(s.tiers[tierKey] || {}), value: value },
      },
    }));
  };

  const applyIncomingData = (incoming) => {
    if (!incoming) return;

    const commonDates = {
      valid_from: asDayjsOrNull(incoming.valid_from),
      valid_until: asDayjsOrNull(incoming.valid_until),
    };

    if (incoming.tiers && typeof incoming.tiers === 'object') {
      const normalized = stripSpecialForBucket(
        bucket,
        { ...dataModel.tiers, ...normalizeIncomingTiers(incoming.tiers) }
      );
      const inferred = inferLimitTypeFromTiers(normalized);
      if (inferred) setSelectedLimitType(inferred);

      setDataModel((s) => ({
        ...s,
        ...incoming,
        ...commonDates,
        tiers: normalized,
      }));
      return;
    }

    setDataModel((s) => ({ ...s, ...incoming, ...commonDates }));
  };

  useEffect(() => {
    if (openType === 'View' || openType === 'Vazgeç' || openType === 'Update') setViewMode(true);
    getCustomerCategories();
    if (data) applyIncomingData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCustomerCategories = async () => {
    await executeGet({
      url: apiUrls.BoostVariants,
      params: { bucket },
      setStateDelegate: setBoostCategories,
    });
  };

  const requiredValidation = () => {
    if (!dataModel.bucket) {
      showSnackbar('Boost Tipi zorunludur.', 'error');
      return false;
    }
    if (!dataModel.category_code) {
      showSnackbar('Boost kategorisi zorunludur.', 'error');
      return false;
    }

    if (bucket === 'period') {
      if (!dataModel.valid_from || !dataModel.valid_until) {
        showSnackbar('Başlangıç/bitiş tarihleri zorunludur.', 'error');
        return false;
      }
      if (dayjs(dataModel.valid_until).isBefore(dayjs(dataModel.valid_from))) {
        showSnackbar('Bitiş tarihi, başlangıç tarihinden önce olamaz.', 'error');
        return false;
      }
    }

    if (bucket === 'special_offer') {
      const qty = String(dataModel?.tiers?.special?.quantity ?? '').trim();
      if (!qty) {
        showSnackbar('Miktar zorunludur.', 'error');
        return false;
      }
      return true;
    }

    const anyQty = Object.entries(dataModel.tiers || {})
      .filter(([k]) => k !== 'special')
      .some(([, t]) => String(t?.quantity ?? '').trim() !== '');
    if (!anyQty) {
      showSnackbar('En az bir kategori için miktar giriniz.', 'error');
      return false;
    }
    return true;
  };

  const createNewBoostRule = async () => {
    if (!requiredValidation()) return;

    const common = {
      bucket: dataModel.bucket,
      variant_id: dataModel.variant_id,
      state: dataModel.isActive ? 'active' : 'inactive',
      ...(bucket !== 'weekly' && {
        valid_from: toIso(dataModel.valid_from),
        valid_until: toIso(dataModel.valid_until),
        ...(dataModel.PeriodDays ? { PeriodDays: Number(dataModel.PeriodDays) } : {}),
      }),
    };

    const payload =
      bucket === 'special_offer'
        ? {
          ...common,
          tiers: {
            special: sanitizeTierOut(
              {
                quantity: dataModel?.tiers?.special?.quantity,
                value: dataModel?.tiers?.special?.value,
                rule_id: dataModel?.tiers?.special?.rule_id,
              },
              selectedLimitType,
              true
            ),
          },
        }
        : {
          ...common,
          tiers: buildNonSpecialTiers(dataModel.tiers, selectedLimitType),
        };

    try {
      const res = await executePost({ url: apiUrls.BoostsRule, data: payload });
      if (res) close && close(res);
    } catch (err) {

      if (err.response.status === 409) {
        showSnackbar("Bu boost kuralı zaten tanımlı.", 'error');
        close && close();
      } else {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Beklenmeyen bir hata oluştu.';
        showSnackbar(msg, 'error');
      }
    }
  };

  const updateBoostStateToActive = () => {
    const ruleIds = Object.values(dataModel?.tiers || {})
      .map((t) => t?.rule_id)
      .filter((x) => Number.isFinite(x));
    executePost({
      url: apiUrls.BoostStatusActivateUpdate,
      data: { rule_ids: ruleIds },
    }).then((res) => { if (res) close && close(res); });
  };

  const updateBoostStateToInactive = () => {
    const ruleIds = Object.values(dataModel?.tiers || {})
      .map((t) => t?.rule_id)
      .filter((x) => Number.isFinite(x));
    executePost({
      url: apiUrls.BoostStatusInactivateUpdate,
      data: { rule_ids: ruleIds },
    }).then((res) => { if (res) close && close(res); });
  };

  const actionList = [
    { name: 'Save', label: 'Kaydet', order: 3 },
    { name: 'Update', label: 'Güncelle', order: 3 },
    { name: 'Vazgeç', variant: 'text', label: 'Vazgeç', order: 0 },
    ...(adminPanelAction
      ? [{ name: adminPanelAction.name, label: adminPanelAction.label, order: 3 }]
      : []),
  ];

  const filteredActionList = () => {
    switch (openType) {
      case 'New':
        return actionList.filter((a) => a.name === 'Save' || a.name === 'Vazgeç');
      case 'View':
        return actionList.filter((a) => a.name === 'Vazgeç');
      case 'Update':
      case 'PasswordUpdate':
      case 'Delete':
        return actionList.filter((a) => a.name === 'Vazgeç' || a.name === adminPanelAction?.name);
      default:
        return actionList.filter((a) => a.name === 'Save' || a.name === 'Vazgeç');
    }
  };

  const onActionClick = useCallback(
    (action) => {
      if (action.name === 'Save') createNewBoostRule();
      else if (action.name === 'StatusInactiveToActiveUpdate') updateBoostStateToActive();
      else if (action.name === 'StatusActiveToInactiveUpdate') updateBoostStateToInactive();
      else if (action.name === 'Vazgeç') close && close();
    },
    [dataModel, selectedLimitType] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: 'auto' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <CardContent
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 2,
          }}
        >
          {/* Boost Category */}
          <Select
            name="BoostCategory"
            label="Boost Kategorisi"
            datasource={boostCategories}
            columns={['category_name']}
            required
            valuePath="category_code"
            readOnly={viewMode}
            value={dataModel?.category_code}
            onChange={(value) => {
              if (viewMode) return;
              const selected = (boostCategories || []).find((c) => c.category_code === value);
              setField({
                category_code: value,
                category_name: selected?.category_name || '',
                variant_id: selected?.id,
              });
            }}
          />

          {/* Boost Tipi (gizlendi) – dataModel.bucket zaten prop üzerinden ayarlanıyor */}

          {/* Quantity - non-special */}
          {bucket !== 'special_offer' && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1,
                alignItems: 'flex-start',
                mt: 2,
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Miktar
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  id="quantity-basic"
                  name="tiers.basic.quantity"
                  fullWidth
                  type="number"
                  placeholder="Basic"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: 1 }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.basic?.quantity ?? ''}
                  onChange={(e) => !viewMode && setTierQty('basic', sanitizePositiveInt(e.target.value))}
                />
                <TextField
                  id="quantity-standard"
                  name="tiers.standard.quantity"
                  fullWidth
                  type="number"
                  placeholder="Standard"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: 1 }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.standard?.quantity ?? ''}
                  onChange={(e) => !viewMode && setTierQty('standard', sanitizePositiveInt(e.target.value))}
                />
                <TextField
                  id="quantity-advanced"
                  name="tiers.advanced.quantity"
                  fullWidth
                  type="number"
                  placeholder="Advanced"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: 1 }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.advanced?.quantity ?? ''}
                  onChange={(e) => !viewMode && setTierQty('advanced', sanitizePositiveInt(e.target.value))}
                />
                <TextField
                  id="quantity-elite"
                  name="tiers.elite.quantity"
                  fullWidth
                  type="number"
                  placeholder="Elite"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: 1 }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.elite?.quantity ?? ''}
                  onChange={(e) => !viewMode && setTierQty('elite', sanitizePositiveInt(e.target.value))}
                />
              </Box>
            </Box>
          )}

          

          {bucket !== 'special_offer' && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1,
                alignItems: 'flex-start',
                mt: 2,
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Değer
              </Typography>

              {/* Değer Türü tam Değer alanlarının üstünde */}
              <Select
                name="LimitType"
                label="Değer Türü"
                datasource={LIMIT_TYPES}
                columns={['ValueText1']}
                required
                valuePath="Code"
                readOnly={viewMode}
                value={selectedLimitType}
                onChange={(value) => !viewMode && setSelectedLimitType(value)}
                xs={12}
                sx={{ p: 0, mb: 1 }}
              />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  id="value-basic"
                  name="tiers.basic.value"
                  fullWidth
                  type="number"
                  placeholder="Basic"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: '0.01' }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.basic?.value ?? ''}
                  onChange={(e) => !viewMode && setTierLimit('basic', sanitizePositiveDecimal(e.target.value))}
                />
                <TextField
                  id="value-standard"
                  name="tiers.standard.value"
                  fullWidth
                  type="number"
                  placeholder="Standard"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: '0.01' }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.standard?.value ?? ''}
                  onChange={(e) => !viewMode && setTierLimit('standard', sanitizePositiveDecimal(e.target.value))}
                />
                <TextField
                  id="value-advanced"
                  name="tiers.advanced.value"
                  fullWidth
                  type="number"
                  placeholder="Advanced"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: '0.01' }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.advanced?.value ?? ''}
                  onChange={(e) => !viewMode && setTierLimit('advanced', sanitizePositiveDecimal(e.target.value))}
                />
                <TextField
                  id="value-elite"
                  name="tiers.elite.value"
                  fullWidth
                  type="number"
                  placeholder="Elite"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: '0.01' }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.elite?.value ?? ''}
                  onChange={(e) => !viewMode && setTierLimit('elite', sanitizePositiveDecimal(e.target.value))}
                />
              </Box>
            </Box>
          )}

          {bucket === 'special_offer' && (
            <>
              {/* Miktar - special */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 1,
                  alignItems: 'flex-start',
                  mt: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Miktar
                </Typography>
                <TextField
                  name="specialDefaultQty"
                  fullWidth
                  required
                  type="number"
                  placeholder="Special"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: 1 }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.special?.quantity ?? ''}
                  onChange={(e) =>
                    !viewMode &&
                    setDataModel((s) => ({
                      ...s,
                      tiers: {
                        ...s.tiers,
                        special: { ...(s.tiers.special || {}), quantity: sanitizePositiveInt(e.target.value) },
                      },
                    }))
                  }
                />
              </Box>

              {/* Değer - special with Değer Türü on top */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 1,
                  alignItems: 'flex-start',
                  mt: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Değer
                </Typography>
                <Select
                  name="LimitType"
                  label="Değer Türü"
                  datasource={LIMIT_TYPES}
                  columns={['ValueText1']}
                  required
                  valuePath="Code"
                  readOnly={viewMode}
                  value={selectedLimitType}
                  onChange={(value) => !viewMode && setSelectedLimitType(value)}
                  xs={12}
                  sx={{ p: 0, mb: 1 }}
                />
                <TextField
                  name="specialValue"
                  fullWidth
                  type="number"
                  placeholder="Special"
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: '0.01' }}
                  onKeyDown={blockInvalidNumberKeys}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={dataModel?.tiers?.special?.value ?? ''}
                  onChange={(e) =>
                    !viewMode &&
                    setDataModel((s) => ({
                      ...s,
                      tiers: {
                        ...s.tiers,
                        special: { ...(s.tiers.special || {}), value: sanitizePositiveDecimal(e.target.value) },
                      },
                    }))
                  }
                />
              </Box>
            </>
          )}

          {bucket !== 'weekly' && (
            <>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="tr"
                localeText={trTR.components.MuiLocalizationProvider.defaultProps.localeText}
              >
                <DateTimePicker
                  ampm={false}
                  format="DD.MM.YYYY HH:mm"
                  label="Başlangıç Tarihi"
                  value={dataModel?.valid_from}
                  onChange={(newValue) => !viewMode && setField({ valid_from: newValue })}
                  readOnly={viewMode}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: bucket === 'period',
                      placeholder: 'DD.MM.YYYY HH:mm',
                    },
                  }}
                />
              </LocalizationProvider>

              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="tr"
                localeText={trTR.components.MuiLocalizationProvider.defaultProps.localeText}
              >
                <DateTimePicker
                  ampm={false}
                  format="DD.MM.YYYY HH:mm"
                  label="Bitiş Tarihi"
                  value={dataModel?.valid_until}
                  onChange={(newValue) => !viewMode && setField({ valid_until: newValue })}
                  readOnly={viewMode}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: bucket === 'period',
                      placeholder: 'DD.MM.YYYY HH:mm',
                    },
                  }}
                  minDateTime={dataModel?.valid_from || undefined}
                />
              </LocalizationProvider>
            </>
          )}
        </CardContent>
      </Box>

      <CardActions
        sx={{
          justifyContent: 'flex-end',
          borderTop: '1px solid #e0e0e0',
          p: 2,
          backgroundColor: '#fff',
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
        }}
      >
        {filteredActionList()
          .sort((a, b) => a.order - b.order)
          .map((action) => (
            <Button
              key={action.name}
              variant={action.variant || 'contained'}
              onClick={() => onActionClick(action)}
              sx={{
                ...(action.variant && {
                  textTransform: 'none',
                  color: '#1976d2',
                  border: 'none',
                  fontWeight: '500',
                  '&:hover': { backgroundColor: '#e3f2fd' },
                }),
                ...(!action.variant && {
                  textTransform: 'none',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  borderRadius: '10px',
                  fontWeight: 'bold',
                }),
              }}
            >
              {action.label}
            </Button>
          ))}
      </CardActions>
    </Card>
  );
};

export default CreateBoost;
